import { ExportableReportType } from 'api-hooks/general/model';
import { useReportExportExcel } from 'api-hooks/report';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import FileSaver from 'file-saver';
import React from 'react';

export default function useExportReport(activeKey) {
  const { mutateAsync, isLoading } = useReportExportExcel();

  const type = React.useMemo(() => {
    switch (activeKey) {
      case NavigationRoutes.SaleReport:
        return ExportableReportType.Sale;
      case NavigationRoutes.PurchaseReport:
        return ExportableReportType.Purchase;
      case NavigationRoutes.AccountReceivableReport:
        return ExportableReportType.AccountReceivable;
      case NavigationRoutes.AccountReceivableRegularReport:
        return ExportableReportType.AccountReceivableRegular;
      case NavigationRoutes.AccountPayableReport:
        return ExportableReportType.AccountPayable;
      case NavigationRoutes.AccountPayableRegularReport:
        return ExportableReportType.AccountPayableRegular;
      case NavigationRoutes.TourReport:
        return ExportableReportType.Tour;
      case NavigationRoutes.CashMutationReport:
        return ExportableReportType.CashMutation;
      case NavigationRoutes.BankMutationReport:
        return ExportableReportType.BankMutation;
      case NavigationRoutes.IncomeReport:
        return ExportableReportType.Income;
      case NavigationRoutes.FlightReport:
        return ExportableReportType.Flights;
      case NavigationRoutes.HotelReport:
        return ExportableReportType.Hotel;
      case NavigationRoutes.ProjectReport:
        return ExportableReportType.Project;
      case NavigationRoutes.TransactionReport:
        return ExportableReportType.TransactionMutation;
      case NavigationRoutes.AgentMutationReport:
        return ExportableReportType.AgentMutation;
      case NavigationRoutes.CustomerMutationReport:
        return ExportableReportType.CustomerMutation;
      case NavigationRoutes.ProfitLossReport:
        return ExportableReportType.ProfitLossMutation;
      case NavigationRoutes.PassportReport:
        return ExportableReportType.Passport;
      case NavigationRoutes.VisaReport:
        return ExportableReportType.Visa;
      case NavigationRoutes.ApecCardReport:
        return ExportableReportType.ApecCard;
      case NavigationRoutes.InsuranceReport:
        return ExportableReportType.Insurance;
      default:
        return undefined;
    }
  }, [activeKey]);

  const permission = React.useMemo(() => {
    switch (activeKey) {
      case NavigationRoutes.SaleReport:
        return AuthorizationRules.ReportsSaleExport;
      case NavigationRoutes.PurchaseReport:
        return AuthorizationRules.ReportsPurchaseExport;
      case NavigationRoutes.AccountReceivableReport:
        return AuthorizationRules.ReportsAccountReceivableExport;
      case NavigationRoutes.AccountReceivableRegularReport:
        return AuthorizationRules.ReportsAccountReceivableRegularExport;
      case NavigationRoutes.AccountPayableReport:
        return AuthorizationRules.ReportsAccountPayableExport;
      case NavigationRoutes.AccountPayableRegularReport:
        return AuthorizationRules.ReportsAccountPayableRegularExport;
      case NavigationRoutes.TourReport:
        return AuthorizationRules.ReportsTourExport;
      case NavigationRoutes.CashMutationReport:
        return AuthorizationRules.ReportsCashMutationExport;
      case NavigationRoutes.BankMutationReport:
        return AuthorizationRules.ReportsBankMutationExport;
      case NavigationRoutes.IncomeReport:
        return AuthorizationRules.ReportsIncomeExport;
      case NavigationRoutes.FlightReport:
        return AuthorizationRules.ReportsFlightExport;
      case NavigationRoutes.HotelReport:
        return AuthorizationRules.ReportsHotelExport;
      case NavigationRoutes.ProjectReport:
        return AuthorizationRules.ReportsProjectExport;
      case NavigationRoutes.TransactionReport:
        return AuthorizationRules.ReportsTransactionMutationExport;
      case NavigationRoutes.AgentMutationReport:
        return AuthorizationRules.ReportsAgentMutationExport;
      case NavigationRoutes.CustomerMutationReport:
        return AuthorizationRules.ReportsCustomerMutationExport;
      case NavigationRoutes.ProfitLossReport:
        return AuthorizationRules.ReportsProfitLossMutationExport;
      case NavigationRoutes.PassportReport:
        return AuthorizationRules.ReportsPassportTravelDocumentExport;
      case NavigationRoutes.VisaReport:
        return AuthorizationRules.ReportsVisaTravelDocumentExport;
      case NavigationRoutes.ApecCardReport:
        return AuthorizationRules.ReportsApecCardTravelDocumentExport;
      case NavigationRoutes.InsuranceReport:
        return AuthorizationRules.ReportsInsuranceTravelDocumentExport;
      default:
        return undefined;
    }
  }, [activeKey]);

  const isHideExport = React.useMemo(() => {
    switch (activeKey) {
      case NavigationRoutes.ProfitLossReport:
      case NavigationRoutes.AgentMutationReport:
      case NavigationRoutes.BankMutationReport:
      case NavigationRoutes.CashMutationReport:
      case NavigationRoutes.CustomerMutationReport:
        return true;
      default:
        return false;
    }
  }, [activeKey]);

  const onExport = React.useCallback(
    async (filter, rows) => {
      const result = await mutateAsync({
        type,
        body: {
          filter,
          rows,
        },
      });
      const fileName = `report-${type}`;
      FileSaver.saveAs(result as Blob, fileName);

      return result;
    },
    [mutateAsync, type],
  );

  return {
    onExport,
    isLoading,
    type,
    permission,
    isHideExport,
  };
}
